import { Button } from '@xo-union/tk-component-buttons';
import Icon from '@xo-union/tk-component-icons';
import { IconName } from '@xo-union/dist-iconography';
import React, { useRef, useState } from 'react';
import { classnames } from '../../helpers/classnames';
import { ActionButton } from './ActionButton';

import styles from './styles.scss';

const ESCAPE_KEY = 'Escape';

type CarrotPositionProps = 'left' | 'right' | 'center';

interface Props {
  size: string;
  color: string;
  actionButton?: Boolean;
  text?: string | React.ReactNode;
  icon?: string;
  showDropIcon?: string;
  className?: string;
  listClassName?: string;
  carrotPosition?: CarrotPositionProps;
}

const ButtonDropdown: React.FC<Props> = ({
  size,
  color = 'primary',
  actionButton,
  text,
  className,
  listClassName,
  icon,
  showDropIcon,
  children,
  carrotPosition = 'right',
  ...rest
}) => {
  const [showDrop, setShowDrop] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // NOTE: to close dropdown on click outside of the component
  const handleOutsideClick = (e: MouseEvent) => {
    if (!e.target) {
      return;
    }
    if (buttonRef.current?.contains(e.target as Node) || dropdownRef.current?.contains(e.target as Node)) {
      return;
    } else {
      setShowDrop(false);
    }
  };

  const handleEscapeClose = (e: KeyboardEvent) => {
    if (buttonRef.current && e.key === ESCAPE_KEY) {
      setShowDrop(false);
      buttonRef.current.focus();
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleEscapeClose);
    return () => document.removeEventListener('keydown', handleEscapeClose);
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const listClass = classnames(styles.buttonDropdownList, {
    [`${listClassName}`]: !!listClassName,
  });

  return (
    <div className={styles.buttonDropdownContainer}>
      {actionButton ? (
        <ActionButton
          text={text}
          expanded={showDrop}
          hasPopup={true}
          onClick={() => {
            setShowDrop(prev => !prev);
          }}
          className={className}
          icon={showDrop && showDropIcon ? (showDropIcon as IconName) : (icon as IconName)}
          size={size}
        />
      ) : (
        <Button
          ref={buttonRef}
          className={className}
          color={color}
          size={size}
          onClick={() => setShowDrop(prev => !prev)}
          data-testid="dropdown_button"
          aria-haspopup="true"
          aria-expanded={showDrop}
          {...rest}
        >
          <div className={styles.buttonCenter}>
            {!!text ? text : null}
            <Icon
              className={styles.arrowIcon}
              name={showDrop && showDropIcon ? (showDropIcon as IconName) : (icon as IconName) || 'arrow_down'}
              size="md"
            />
          </div>
        </Button>
      )}
      {showDrop && (
        <div ref={dropdownRef} className={listClass} data-testid="dropdown_list">
          <ul
            className={carrotPosition === 'right' ? styles.list : classnames(styles.list, styles[carrotPosition])}
            role="menu"
          >
            {React.Children.map(children, (child: any) => {
              if (child) {
                return React.cloneElement(child, { closeDropdown: () => setShowDrop(false) });
              }

              return null;
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ButtonDropdown;
